"use client";

import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";
import { buttonClass } from "@/components/ui/button";
import { formatIdrShort } from "@/lib/dataset/price-label";
import {
	DATA_FREE_LAYER_FLAG,
	type DataDepthSurface,
	freeLayerSummary,
	GATED_FIELDS,
	paidDepthUrl,
} from "@/lib/dataset/tiers";
import { DATASET_PRODUCTS } from "@/lib/mayar/products";

type Locale = "en" | "id";

/**
 * The one "full depth" block on a public data surface (lane L8, PR 10): names
 * what is free on the page, lists exactly what the Full Dataset CSV adds, and
 * links the /data storefront (Mayar checkout) with `utm_campaign=data_depth_<surface>`.
 *
 * Sole switch: PostHog flag `data-free-layer`. Off/absent ⇒ renders nothing, so
 * the page is exactly today's page. A client island on purpose: the host pages
 * are ISR/edge-cached, so the flag must not be read server-side (no per-visitor
 * HTML, no `Set-Cookie`). `useFeatureFlagEnabled` subscribes, so a cold first
 * visit paints once flags land (the `report-checkout` invisible-CTA lesson in
 * report-purchase.tsx).
 */
export function DataDepthCta({
	surface,
	locale,
}: {
	surface: DataDepthSurface;
	locale: Locale;
}) {
	const enabled = useFeatureFlagEnabled(DATA_FREE_LAYER_FLAG) === true;
	const posthog = usePostHog();
	const viewedRef = useRef(false);
	const isId = locale === "id";

	useEffect(() => {
		if (!enabled || viewedRef.current) return;
		viewedRef.current = true;
		posthog?.capture("data_depth_cta_viewed", { surface, locale });
	}, [enabled, posthog, surface, locale]);

	if (!enabled) return null;

	const full = DATASET_PRODUCTS.dataset_full;
	const price = isId
		? formatIdrShort(full.amountIdr)
		: `$${(full.amountUsdMinor / 100).toFixed(0)}`;
	const headingId = `data-depth-${surface}`;

	return (
		<section
			aria-labelledby={headingId}
			data-testid="data-depth-cta"
			className="mt-10 rounded-lg border border-border bg-card p-5 text-card-foreground"
		>
			<h2 id={headingId} className="text-[15px] font-semibold">
				{isId
					? `Data lengkap di balik halaman ini: ${full.label.id}, ${price}`
					: `The full data behind this page: ${full.label.en}, ${price}`}
			</h2>
			<p className="mt-2 text-[13px] text-muted-foreground">
				{freeLayerSummary(locale)}
			</p>
			<ul className="mt-3 list-disc space-y-1 pl-5 text-[13px]">
				{GATED_FIELDS.map((f) => (
					<li key={f.en}>{isId ? f.id : f.en}</li>
				))}
			</ul>
			<a
				href={paidDepthUrl(surface, locale)}
				onClick={() =>
					posthog?.capture("data_depth_cta_clicked", { surface, locale })
				}
				className={buttonClass({ size: "sm", className: "mt-4" })}
			>
				{isId ? "Lihat dataset lengkap" : "See the full dataset"}
			</a>
		</section>
	);
}
